import React from 'react';
import classNames from 'classnames';

export type DarkDataTableColumn<RowType> = {
  header: React.ReactNode;
  render: (row: RowType) => React.ReactNode;
  thClassName?: string;
  tdClassName?: string;
};

export interface DarkDataTableProps<RowType> {
  columns: Array<DarkDataTableColumn<RowType>>;
  rows: RowType[];
  getRowKey: (row: RowType, index: number) => string;

  /**
   * When true (default), renders a rounded/bordered container around the table.
   * Set to false when the surrounding panel already provides the frame.
   */
  frame?: boolean;

  onRowClick?: (row: RowType) => void;
  isRowClickable?: (row: RowType) => boolean;
  isRowSelected?: (row: RowType) => boolean;

  /**
   * Cursor style for clickable rows. Default is 'pointer'.
   * Use 'default' when you want rows clickable without a hand cursor.
   */
  clickCursor?: 'pointer' | 'default' | 'none';

  isRowExpanded?: (row: RowType) => boolean;
  renderExpandedRow?: (row: RowType) => React.ReactNode;

  emptyMessage?: React.ReactNode;
}

export default function DarkDataTable<RowType>({
  columns,
  rows,
  getRowKey,
  frame = true,
  onRowClick,
  isRowClickable,
  isRowSelected,
  clickCursor = 'pointer',
  isRowExpanded,
  renderExpandedRow,
  emptyMessage = 'No data.',
}: DarkDataTableProps<RowType>) {
  const table = (
    <table className="w-full text-left text-xs">
        <thead className="border-b border-neutral-800">
          <tr>
            {columns.map((c, idx) => (
              <th
                key={idx}
                className={classNames('px-2 py-1 text-neutral-500 font-medium', c.thClassName)}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-2 py-2 text-neutral-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => {
              const key = getRowKey(row, idx);
              const selected = isRowSelected ? isRowSelected(row) : false;
              const clickable = Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true);
              const expanded = isRowExpanded ? isRowExpanded(row) : false;
              const clickCursorClass =
                !clickable || clickCursor === 'none'
                  ? ''
                  : (clickCursor === 'pointer' ? 'cursor-pointer' : 'cursor-default');

              return (
                <React.Fragment key={key}>
                  <tr
                    className={classNames(
                      'group',
                      clickCursorClass,
                      selected ? 'bg-white/[0.04]' : '',
                      clickable ? 'hover:bg-white/[0.02]' : ''
                    )}
                    onClick={() => clickable && onRowClick?.(row)}
                  >
                    {columns.map((c, idx) => (
                      <td key={idx} className={classNames('px-2 py-1 align-top', c.tdClassName)}>
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>

                  {expanded && renderExpandedRow && (
                    <tr className={selected ? 'bg-white/[0.04]' : ''}>
                      <td colSpan={columns.length} className="p-0">
                        {renderExpandedRow(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
    </table>
  );

  if (!frame) return table;

  return (
    <div className="rounded-md border border-neutral-800 overflow-hidden">
      {table}
    </div>
  );
}
